import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ICommitRejection } from '@apis/schedule';
import { Checkbox } from '@components/ui/Checkbox/Checkbox';
import { ProfessionPill } from '@components/ui/ProfessionPill/ProfessionPill';
import { SegmentedControl } from '@components/ui/SegmentedControl/SegmentedControl';
import type { SiteId, TaskId, TaskKind } from '@interfaces/IScheduler';
import { useSiteLocations } from '@hooks/useSiteLocations';
import { useCommitAssignees } from '../../hooks/useCommitAssignees';
import { useSchedulerSite, useSchedulerStale, useSchedulerTask } from '../../hooks/useSchedulerStore';
import { useScheduleDirty } from '../../hooks/useScheduleDirty';
import { useTaskCommitActions } from '../../hooks/useTaskCommitActions';
import { useTaskLifecycleActions } from '../../hooks/useTaskLifecycleActions';
import { useSelector } from 'react-redux';
import { useSiteToday } from '@d-orca/shared/client/hooks';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { areaSummary } from '../../lib/areaSummary';
import { draftFromTask, draftGaps, draftToForm, draftWithKind, type ICommitDraft } from '../../lib/commitDraft';
import { ProfessionPicker } from '../TaskPanel/ProfessionPicker';
import { AreaGridPicker } from './AreaGridPicker';
import { AssigneeChips } from './AssigneeChips';
import { ReviewFooter } from './ReviewFooter';
import { ReviewRow } from './ReviewRow';
import { ChecklistReviewRow } from './ChecklistReviewRow';
import { CommitRejections } from './CommitRejections';
import styles from './CommitReview.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

// ---------------------------------------------------------------------------
// CommitReview — the promise as Trusstor will receive it, with its gaps
// editable in place (2026-09-18 commit UX design, decision 3). Replaces the
// two-field modal: every row is either filled (✓ and its value) or needed
// (○ and its editor, already open). Nobody leaves the review to satisfy a
// precondition; the commit request carries what was filled and the server
// writes it to the plan before making the promise.
// ---------------------------------------------------------------------------

export interface ICommitReviewProps {
  siteId: SiteId;
  taskId: TaskId;
  scheduleNumber?: string;
  /** Leave the review — after a commit, or on Cancel. */
  onClose: () => void;
}

/** The two kinds of promise, in the order the switch offers them. */
const KINDS: readonly TaskKind[] = ['field', 'administrative'];

export function CommitReview({ siteId, taskId, scheduleNumber, onClose }: ICommitReviewProps) {
  const { t } = useTranslation();
  const ids = useId();
  // "Starts in the past" is judged on the SITE's calendar, not the browser's.
  const today = useSiteToday(useSelector(selectSiteTz));
  const site = useSchedulerSite(siteId);
  const task = useSchedulerTask(siteId, taskId);
  const dirty = useScheduleDirty(siteId, scheduleNumber);
  const stale = useSchedulerStale(siteId);
  const catalog = useSiteLocations(siteId);
  const { setStatus } = useTaskLifecycleActions(siteId, taskId, scheduleNumber);
  const { commit, pending } = useTaskCommitActions({ siteId, taskId, scheduleNumber });

  // The project's users — the assignee chips, and the caller as the default.
  const { candidates, me, settled } = useCommitAssignees(siteId);

  // The draft is minted ONCE, when both the task and the default assignee are
  // known (render-time state adjust, not an effect). Edits go to it alone.
  const [draft, setDraft] = useState<ICommitDraft | null>(null);
  const [rejections, setRejections] = useState<ICommitRejection[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(false);
  if (draft === null && task && settled) setDraft(draftFromTask(task, me));

  if (!task || !draft) return <div className={styles.loading}>{t('scheduler.commit.loading')}</div>;

  const parent = task.parentId ? site?.tasks.find((row) => row.id === task.parentId) : undefined;
  const gaps = draftGaps(draft, task.status);
  const isAdmin = draft.kind === 'administrative';
  const pastStart = task.start < today;
  const blocked = task.status === 'blocked';
  const patch = (next: Partial<ICommitDraft>) => setDraft({ ...draft, ...next });

  const submit = async () => {
    const outcome = await commit(draftToForm(draft, task));
    setRejections(outcome.rejections);
    setFailure(outcome.error ?? null);
    // Only a made promise closes the review. A refusal keeps its to-fix list
    // on screen; a failed call keeps the answers so the retry is one click.
    if (outcome.ok) onClose();
  };

  const reason = blocked
    ? t('scheduler.commit.blockedHint')
    : stale
      ? t('scheduler.stale.banner')
      : dirty
        ? t('scheduler.commit.saveFirst')
        : gaps.length > 0
          ? t('scheduler.commit.stillMissing', { count: gaps.length })
          : t(isAdmin ? 'scheduler.commit.readyAdministrative' : 'scheduler.commit.readyField');
  const canCommit = !blocked && !stale && !dirty && gaps.length === 0 && !pending;

  return (
    <div className={styles.root} data-testid="commit-review">
      <ScrollArea className={styles.body} viewportClassName={styles.bodyContent}>
        <p className={styles.intro}>{t('scheduler.commit.reviewIntro')}</p>

        {blocked && (
          <div className={styles.note} role="status">
            <span>{t('scheduler.commit.blockedNote', { reason: '' })}</span>
            <button type="button" className={styles.linkBtn} onClick={() => setStatus('planned')}>
              {t('scheduler.commit.unblock')}
            </button>
          </div>
        )}

        <div className={styles.kind}>
          <SegmentedControl<TaskKind>
            value={draft.kind}
            onChange={(kind) => setDraft(draftWithKind(draft, kind))}
            options={KINDS.map((kind) => ({ id: kind, label: t(`scheduler.commit.kind.${kind}`) }))}
            ariaLabel={t('scheduler.commit.row.kind')}
          />
        </div>

        <ReviewRow state="ok" label={t('scheduler.commit.row.task')}>
          <b>{task.name}</b>
          {parent && <span className={styles.muted}> · {t('scheduler.commit.under', { name: parent.name })}</span>}
        </ReviewRow>

        {!isAdmin && (
          <ReviewRow state={draft.profession ? 'ok' : 'need'} label={t('scheduler.commit.row.profession')}>
            {draft.profession ? (
              <span className={styles.inline}>
                <ProfessionPill profession={draft.profession} showContractor />
                <button type="button" className={styles.linkBtn} onClick={() => patch({ profession: undefined })}>
                  {t('scheduler.commit.change')}
                </button>
              </span>
            ) : (
              <>
                <p className={styles.prompt}>{t('scheduler.commit.needed.profession')}</p>
                <ProfessionPicker id={`${ids}-profession`} siteId={siteId} value={undefined} onChange={(profession) => patch({ profession })} />
              </>
            )}
          </ReviewRow>
        )}

        {!isAdmin && (
          <ReviewRow state={draft.areaIds.length > 0 ? 'ok' : 'need'} label={t('scheduler.commit.row.areas')}>
            {draft.areaIds.length > 0 ? (
              <span className={styles.inline}>
                <span>{areaSummary(draft.areaIds, catalog.floors, t)}</span>
                <button type="button" className={styles.linkBtn} onClick={() => setShowMap((open) => !open)}>
                  {t(showMap ? 'scheduler.commit.hideMap' : 'scheduler.commit.change')}
                </button>
              </span>
            ) : (
              <p className={styles.prompt}>{t('scheduler.commit.needed.areas')}</p>
            )}
            {/* An empty row opens the map by itself; a pick latches it open, or
                the first area filling the row would fold the map away mid-pick. */}
            {(draft.areaIds.length === 0 || showMap) && (
              <div className={styles.map}>
                <AreaGridPicker
                  floors={catalog.floors}
                  value={draft.areaIds}
                  onChange={(areaIds) => {
                    patch({ areaIds });
                    setShowMap(true);
                  }}
                />
                <p className={styles.hint}>{t('scheduler.commit.mapHint')}</p>
              </div>
            )}
          </ReviewRow>
        )}

        <ReviewRow state="info" label={t('scheduler.commit.row.dates')}>
          <span>
            {task.start} → {task.end}
            <span className={styles.muted}> · {t('scheduler.commit.datesHint')}</span>
          </span>
          {pastStart && !isAdmin && <p className={`${styles.note} ${styles.warn}`}>{t('scheduler.commit.pastStart')}</p>}
        </ReviewRow>

        <ChecklistReviewRow checklist={task.checklist} />

        <ReviewRow state={draft.assignee ? 'ok' : 'info'} label={t('scheduler.commit.row.assignee')}>
          <AssigneeChips candidates={candidates} value={draft.assignee} onPick={(assignee) => patch({ assignee })} />
        </ReviewRow>

        {!isAdmin && (
          <ReviewRow state="info" label={t('scheduler.commit.row.safety')}>
            <label className={styles.check}>
              <Checkbox
                checked={draft.dangerousActivity}
                onCheckedChange={(dangerousActivity) => patch({ dangerousActivity })}
                ariaLabel={t('scheduler.commit.dangerous')}
              />
              <span>
                {t('scheduler.commit.dangerous')}
                <small className={styles.hint}>{t('scheduler.commit.dangerousHint')}</small>
              </span>
            </label>
          </ReviewRow>
        )}

        <CommitRejections rejections={rejections} />
      </ScrollArea>

      <ReviewFooter
        reason={reason}
        error={failure}
        canCommit={canCommit}
        pending={pending}
        confirmLabel={t('scheduler.commit.confirm')}
        confirmTestId="commit-confirm"
        onCancel={onClose}
        onConfirm={() => void submit()}
      />
    </div>
  );
}
